/**
 * KAN-756: чистые функции доски CRM: заголовки дней, деньги, ссылка Telegram,
 * адаптер строки доски к блоку "Требуют внимания" (действия и модалка
 * "Взять в работу" переиспользуются с дашборда).
 */
import type { DialogTarget } from '@/hooks/useDialogParam';
import { attentionItemTitle } from '@/components/dashboard/attentionFormat';
import type { AttentionAction, AttentionItem } from '@/services/api/dashboard';
import type { CrmBoard, CrmDay, CrmOutcome, CrmRow } from '@/types/crm';
import { formatPrice } from './catalogTree';
import { DEFAULT_ORG_TIMEZONE, zonedDayKey, zonedParts, type ZonedParts } from './orgTimezone';

/** Прочерк пустой ячейки таблицы. */
export const EMPTY_CELL = '-';

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LONG_MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Ключ дня ГГГГ-ММ-ДД по календарю (UTC-компоненты Date). */
function utcDayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * "2026-10-09" -> "Сегодня" / "Вчера" / "9 октября" (другой год: "9 октября 2025").
 * Дата группы считается по Москве, поэтому и "сегодня" берётся по Москве,
 * а не по часовому поясу браузера.
 */
export function formatCrmDayTitle(date: string, now: Date = new Date()): string {
  const match = DAY_RE.exec(date);
  if (!match) return date;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const dayOfMonth = Number(match[3]);
  if (month < 1 || month > 12) return date;
  const msk = moscowParts(now);
  const todayUtc = new Date(Date.UTC(msk.year, msk.month - 1, msk.day));
  if (date === utcDayKey(todayUtc)) return 'Сегодня';
  const yesterdayUtc = new Date(Date.UTC(msk.year, msk.month - 1, msk.day - 1));
  if (date === utcDayKey(yesterdayUtc)) return 'Вчера';
  const label = `${dayOfMonth} ${LONG_MONTHS[month - 1]}`;
  return year !== msk.year ? `${label} ${year}` : label;
}

/** Дни от свежего к старому (копия массива). */
export function sortCrmDays(days: readonly CrmDay[]): CrmDay[] {
  return [...days].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/** Сумма заказа: "1 500 ₽"; пустая или не число: "". */
export function formatCrmMoney(total: number | string | null | undefined, currency: string | null | undefined): string {
  if (total === null || total === undefined || total === '') return '';
  const amount = Number(total);
  if (!Number.isFinite(amount)) return '';
  return formatPrice(amount, currency);
}

/** Ссылка на Telegram, только https://t.me/...; иначе null. */
export function safeTelegramLink(link: string | null | undefined): string | null {
  if (typeof link !== 'string') return null;
  const raw = link.trim();
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && url.hostname === 't.me' ? raw : null;
  } catch {
    return null;
  }
}

/** "ivan" / "@ivan" -> "@ivan"; пусто: null. */
export function telegramLabel(username: string | null | undefined): string | null {
  const name = (username ?? '').trim().replace(/^@+/, '');
  return name ? `@${name}` : null;
}

const SHORT_MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/**
 * Части даты по Москве: год, месяц (1..12), день, час, минута. Дни доски и
 * периоды считает сервер по Москве; время клиента (KAN-840) идёт через lib/orgTimezone.
 */
export function moscowParts(date: Date): ZonedParts {
  return zonedParts(date, DEFAULT_ORG_TIMEZONE);
}

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Следующая связь по времени организации: "10 окт, 14:00"; пусто или мусор: null. */
export function formatNextContact(
  iso: string | null | undefined,
  timeZone: string = DEFAULT_ORG_TIMEZONE,
): string | null {
  const date = parseIso(iso);
  if (!date) return null;
  const p = zonedParts(date, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.day} ${SHORT_MONTHS[p.month - 1]}, ${pad(p.hour)}:${pad(p.minute)}`;
}

/** KAN-883: дата и время коротко цифрами по времени организации: "21.10 14:30"; пусто или мусор: null. */
export function formatShortDateTime(
  iso: string | null | undefined,
  timeZone: string = DEFAULT_ORG_TIMEZONE,
): string | null {
  const date = parseIso(iso);
  if (!date) return null;
  const p = zonedParts(date, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(p.day)}.${pad(p.month)} ${pad(p.hour)}:${pad(p.minute)}`;
}

/** Связь пора делать: дата по времени организации сегодня или раньше. */
export function isNextContactDue(
  iso: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = DEFAULT_ORG_TIMEZONE,
): boolean {
  const date = parseIso(iso);
  if (!date) return false;
  return zonedDayKey(date, timeZone) <= zonedDayKey(now, timeZone);
}

/** Строка доски как элемент блока внимания: для хуков и модалки "Взять в работу". */
export function crmRowToAttentionItem(row: CrmRow): AttentionItem {
  return {
    client_id: row.client_id,
    name: row.contact_name?.trim() || row.phone?.trim() || '',
    company: row.clinic?.trim() || '',
    reasons: [],
    reason_text: row.reason || '',
    since: row.done_at || '',
    last_message: null,
    sender_account: null,
    estimate_id: null,
    manager: row.manager ? { id: row.manager.id, full_name: row.manager.name } : null,
    // KAN-790: задача менеджеру; старый backend поле не шлёт.
    task: row.task ?? null,
  };
}

/** Оптимистичное снятие строки из блока внимания доски (дни обновит сервер). */
export function withoutAttentionRow(board: CrmBoard, clientId: string): CrmBoard {
  return { ...board, attention: board.attention.filter((row) => row.client_id !== clientId) };
}

const ACTION_OUTCOME: Partial<Record<AttentionAction, CrmOutcome>> = { success: 'won', refused: 'lost' };

/**
 * KAN-786: оптимистичное действие над строкой доски. Строка уходит из блока
 * внимания; "Успех"/"Отказ" сразу проставляют исход отработанной строке
 * (кнопки исхода пропадают до ответа сервера, при ошибке кеш откатывается).
 */
export function withCrmRowAction(board: CrmBoard, clientId: string, action: AttentionAction): CrmBoard {
  const next = withoutAttentionRow(board, clientId);
  const outcome = ACTION_OUTCOME[action];
  if (!outcome) return next;
  return {
    ...next,
    days: next.days.map((day) => ({
      ...day,
      rows: day.rows.map((row) => (row.client_id === clientId ? { ...row, outcome } : row)),
    })),
  };
}

/**
 * KAN-765: модалка переписки клиента строки. Заголовок тот же, что у модалки
 * "Взять в работу" (attentionItemTitle над адаптером строки), без своего порядка.
 */
export function crmDialogTarget(row: CrmRow): DialogTarget {
  return { clientId: row.client_id, hint: { title: attentionItemTitle(crmRowToAttentionItem(row)) } };
}
